import { safeLocalStorage } from '@toss/storage';

export interface PlanChangeParams {
  userId?: string;
  workspaceHashId?: string;
  quizPlanType?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Storage 인터페이스 (DI용)
 */
export interface StorageAdapter {
  get: (key: string) => string | null;
  set: (key: string, value: string) => void;
  remove: (key: string) => void;
}

/**
 * 기본 localStorage 어댑터
 */
export const defaultStorageAdapter: StorageAdapter = {
  get: key => safeLocalStorage.get(key),
  set: (key, value) => safeLocalStorage.set(key, value),
  remove: key => safeLocalStorage.remove(key),
};

const PLAN_ID_KEY = 'current-plan-id';

/**
 * 플랜 ID 생성
 */
const generatePlanId = (params: PlanChangeParams): string => {
  const { userId, workspaceHashId, quizPlanType, startDate, endDate } = params;
  return `${userId || ''}_${workspaceHashId || ''}_${quizPlanType || ''}_${
    startDate || ''
  }_${endDate || ''}`;
};

/**
 * 플랜이 변경되었는지 확인하고, 변경되었으면 스토리지들을 초기화합니다.
 * @returns 플랜이 변경되었으면 true, 아니면 false
 */
export function checkPlanChangedAndReset(
  params: PlanChangeParams,
  storages: { reset: () => void }[],
  storage: StorageAdapter = defaultStorageAdapter,
): boolean {
  const currentPlanId = generatePlanId(params);

  try {
    const prevPlanId = storage.get(PLAN_ID_KEY);
    const isPlanChanged = !!prevPlanId && prevPlanId !== currentPlanId;

    if (isPlanChanged) {
      console.warn('[PlanAlert] Plan changed, resetting storages');
      storages.forEach(s => s.reset());
    }

    storage.set(PLAN_ID_KEY, currentPlanId);
    return isPlanChanged;
  } catch (e) {
    console.error('[PlanAlert] Error in checkPlanChangedAndReset', e);
    return false;
  }
}

/**
 * 플랜 기반 알림 스토리지를 위한 공통 유틸리티 함수
 */
export function createPlanAlertStorage<T>(
  storageKey: string,
  storage: StorageAdapter = defaultStorageAdapter,
) {
  /**
   * 스토리지 초기화
   */
  const reset = (): void => {
    try {
      storage.remove(storageKey);
    } catch {
      // storage 오류 무시
    }
  };

  /**
   * 상태를 가져옵니다.
   */
  const getState = (): T => {
    try {
      const stored = storage.get(storageKey);
      return stored ? JSON.parse(stored) : ({} as T);
    } catch {
      return {} as T;
    }
  };

  /**
   * 상태를 저장합니다.
   */
  const setState = (state: T): void => {
    try {
      storage.set(storageKey, JSON.stringify(state));
    } catch {
      // storage 오류 무시
    }
  };

  return {
    reset,
    getState,
    setState,
  };
}
